/**
 * Helpers for rendering the v3 Classification block (Task 3508 follow-up).
 *
 * The `content_classifier` agent persists a structured Classification on
 * every classified Interaction. This module centralises:
 *   - The short human labels for each enum (no emojis per CLAUDE.md)
 *   - The colour tokens used by inbox badges/chips
 *   - Backward-compatibility helpers that accept either the v3 object or
 *     the v2 compact string (the latter still appears on Firestore rows
 *     older than the 7-day rollout window, and on the legacy summary
 *     projection of GET /v1/interactions).
 *
 * Field semantics are mirrored from
 * services/communications/api/openapi.yaml — Classification schema.
 */

import type {
  Classification,
  CustomerTone,
  Interaction,
  InteractionClassification,
  InteractionClassificationField,
  PrimaryIntent,
  RelatedEntityKind,
  RequiredAction,
  SentimentLabel,
  Urgency,
} from './types'

// ── Normalisation ────────────────────────────────────────────────────────────

/**
 * Returns the structured Classification block (or undefined) regardless of
 * whether the backend supplied the compact summary projection (plain string)
 * or the full Classification object (`view=full`). Always defensive.
 */
export function getClassification(
  i: Pick<Interaction, 'classification'>,
): Classification | undefined {
  const c = i.classification as InteractionClassificationField | undefined
  if (!c) return undefined
  if (typeof c === 'string') return undefined
  return c
}

/**
 * Returns the canonical category string for an interaction, accepting either
 * the v3 object shape or the legacy plain-string shape. Falls back to
 * `'unclassified'` when neither is present so downstream consumers don't
 * have to guard.
 */
export function getCategory(
  i: Pick<Interaction, 'classification'>,
): InteractionClassification | string {
  const c = i.classification as InteractionClassificationField | undefined
  if (!c) return 'unclassified'
  if (typeof c === 'string') return c
  return c.category ?? 'unclassified'
}

/**
 * Returns the canonical primary-entity slug, falling back to the deprecated
 * v2 `related_app` field for older Firestore rows. Rendering only — callers
 * must not write `related_app` anywhere.
 */
export function getRelatedEntity(c?: Classification): string | null {
  if (!c) return null
  if (c.related_entity && c.related_entity_kind !== 'none')
    return c.related_entity
  if (c.related_app) return c.related_app
  return null
}

export function getRelatedEntityKind(
  c?: Classification,
): RelatedEntityKind | undefined {
  if (!c) return undefined
  if (c.related_entity_kind && c.related_entity_kind !== 'none')
    return c.related_entity_kind
  // Fallback path: v2 documents only carried `related_app`, which by
  // construction was an app slug. Surface that as the entity kind so the
  // detail view can still label the row correctly.
  if (c.related_app) return 'app'
  return undefined
}

// ── Sentiment ────────────────────────────────────────────────────────────────

export const SENTIMENT_LABEL: Record<SentimentLabel, string> = {
  very_negative: 'Very negative',
  negative: 'Negative',
  neutral: 'Neutral',
  positive: 'Positive',
  very_positive: 'Very positive',
}

export const SENTIMENT_SHORT: Record<SentimentLabel, string> = {
  very_negative: 'NEG--',
  negative: 'NEG',
  neutral: 'NEU',
  positive: 'POS',
  very_positive: 'POS++',
}

/**
 * Visual weighting for sentiment badges. The colour tokens are the same
 * semantic tokens used elsewhere in Stream (status pills, action
 * buttons) — no new visual primitives.
 */
export const SENTIMENT_COLOR: Record<SentimentLabel, string> = {
  very_negative: 'var(--error)',
  negative: 'var(--warning)',
  neutral: 'var(--text-muted)',
  positive: 'var(--success)',
  very_positive: 'var(--success)',
}

// ── Urgency ──────────────────────────────────────────────────────────────────

export const URGENCY_LABEL: Record<Urgency, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
}

export const URGENCY_COLOR: Record<Urgency, string> = {
  low: 'var(--text-muted)',
  medium: 'var(--info)',
  high: 'var(--warning)',
  critical: 'var(--error)',
}

export const URGENCY_ORDER: Record<Urgency, number> = {
  low: 0,
  medium: 1,
  high: 2,
  critical: 3,
}

// ── Required action ─────────────────────────────────────────────────────────

export const REQUIRED_ACTION_LABEL: Record<RequiredAction, string> = {
  mark_processed_no_reply: 'Auto-close',
  auto_acknowledge_only: 'Ack only',
  reply_required: 'Reply required',
  escalate_to_human: 'Escalate',
  awaiting_more_info: 'Awaiting info',
  not_actionable: 'Not actionable',
  // v3.2.0 review-channel values (Task 3545b)
  respond_publicly: 'Respond',
  appeal_to_apple: 'Appeal',
  skip_no_response: 'Skip',
}

export const REQUIRED_ACTION_COLOR: Record<RequiredAction, string> = {
  mark_processed_no_reply: 'var(--text-muted)',
  auto_acknowledge_only: 'var(--text-muted)',
  reply_required: 'var(--accent-primary)',
  escalate_to_human: 'var(--error)',
  awaiting_more_info: 'var(--warning)',
  not_actionable: 'var(--text-muted)',
  // v3.2.0 review-channel values (Task 3545b)
  respond_publicly: 'var(--accent-primary)',
  appeal_to_apple: 'var(--warning)',
  skip_no_response: 'var(--text-muted)',
}

// ── Customer tone ───────────────────────────────────────────────────────────

export const CUSTOMER_TONE_LABEL: Record<CustomerTone, string> = {
  cordial: 'Cordial',
  neutral: 'Neutral',
  confused: 'Confused',
  frustrated: 'Frustrated',
  angry: 'Angry',
  threatening: 'Threatening',
  sarcastic: 'Sarcastic',
  appreciative: 'Appreciative',
}

export const CUSTOMER_TONE_COLOR: Record<CustomerTone, string> = {
  cordial: 'var(--success)',
  neutral: 'var(--text-muted)',
  confused: 'var(--info)',
  frustrated: 'var(--warning)',
  angry: 'var(--error)',
  threatening: 'var(--error)',
  sarcastic: 'var(--warning)',
  appreciative: 'var(--success)',
}

// ── Primary intent (v3.1, Task 3509) ────────────────────────────────────────

export const PRIMARY_INTENT_LABEL: Record<PrimaryIntent, string> = {
  support_question: 'Support question',
  bug_report: 'Bug report',
  feature_request: 'Feature request',
  cancellation_request: 'Cancellation',
  refund_request: 'Refund',
  restore_purchase: 'Restore purchase',
  billing_inquiry: 'Billing inquiry',
  account_inquiry: 'Account inquiry',
  feedback_positive: 'Positive feedback',
  feedback_negative: 'Negative feedback',
  business_inquiry: 'Business inquiry',
  security_report: 'Security report',
  acknowledgement: 'Acknowledgement',
  follow_up: 'Follow-up',
  noise: 'Noise',
  other: 'Other',
}

/**
 * Visual weight for primary-intent chips. Mostly muted because the chip sits
 * alongside urgency/required_action in the headline row; only the high-stakes
 * intents (security/cancellation/refund) lean on saturated colour tokens.
 */
export const PRIMARY_INTENT_COLOR: Record<PrimaryIntent, string> = {
  support_question: 'var(--text-muted)',
  bug_report: 'var(--warning)',
  feature_request: 'var(--info)',
  cancellation_request: 'var(--error)',
  refund_request: 'var(--error)',
  restore_purchase: 'var(--warning)',
  billing_inquiry: 'var(--info)',
  account_inquiry: 'var(--info)',
  feedback_positive: 'var(--success)',
  feedback_negative: 'var(--warning)',
  business_inquiry: 'var(--text-muted)',
  security_report: 'var(--error)',
  acknowledgement: 'var(--text-muted)',
  follow_up: 'var(--text-muted)',
  noise: 'var(--text-muted)',
  other: 'var(--text-muted)',
}

// ── Related entity kind ─────────────────────────────────────────────────────

export const RELATED_ENTITY_KIND_LABEL: Record<RelatedEntityKind, string> = {
  app: 'App',
  partner: 'Partner',
  platform_area: 'Platform area',
  cohort: 'Cohort',
  other: 'Other',
  none: 'None',
}

// ── Sentiment score helpers ─────────────────────────────────────────────────

/**
 * Returns the numeric sentiment score [-1, 1] for an interaction, or
 * `null` when the score is missing. Used for sort comparators.
 */
export function getSentimentScore(i: Interaction): number | null {
  const c = getClassification(i)
  const s = c?.sentiment?.score
  return typeof s === 'number' && Number.isFinite(s) ? s : null
}

/**
 * Convenience accessor mirroring `getSentimentScore` but for the urgency
 * ordinal. Returns `null` for missing urgency so we can sort it to the end.
 */
export function getUrgencyRank(i: Interaction): number | null {
  const c = getClassification(i)
  if (!c?.urgency) return null
  return URGENCY_ORDER[c.urgency] ?? null
}
